import { LogType } from '@tencent/aegis-web-sdk-v2'
import { UserInfo } from '@types'
import { message } from 'antd'

/**
 * T3C 状态检查参数
 */
export interface T3CCheckParams {
  t3cReady: boolean
  t3cSdkReady: boolean
  t3cfailed: boolean
  t3cErrorMsg: string
  curTabUUID: string
  t3cSdkReadyTime: string
  t3cSdkUrl: string
  userEmail: string
  useT3C: boolean
  reinitT3cSdk: (params?: { refresh?: boolean }) => void
}

/**
 * T3C 状态检查结果
 */
export interface T3CCheckResult {
  canCall: boolean // 是否可以外呼
  errorMsg?: string // 错误信息（如果不可以外呼）
  needReinit?: boolean // 是否需要重新初始化
}

/**
 * 检查 T3C 状态是否可以外呼
 * @param params T3C 状态参数
 * @returns 检查结果
 */
export function checkT3CCallReady (params: T3CCheckParams): T3CCheckResult {
  const {
    t3cReady,
    t3cSdkReady,
    t3cfailed,
    t3cErrorMsg,
    curTabUUID,
    t3cSdkReadyTime,
    t3cSdkUrl,
    userEmail,
    useT3C,
    reinitT3cSdk,
  } = params

  const debugInfo = `tabUUID: ${curTabUUID}; userId: ${userEmail};当前时间: ${new Date().toLocaleString()}; 脚本加载时间: ${t3cSdkReadyTime}; t3cSdkUrl: ${t3cSdkUrl}`

  // 检查是否启用 T3C
  if (!useT3C) {
    return {
      canCall: false,
      errorMsg: '当前未启用T3C服务，无法进行外呼',
    }
  }

  // 邮箱验证：检查用户是否绑定邮箱
  const hasEmail = userEmail && userEmail.trim() !== ''
  if (!hasEmail) {
    return {
      canCall: false,
      errorMsg: '当前账号未绑定邮箱,无法接入 TCCC 服务,如需外呼服务请联系业务管理员',
    }
  }

  // SDK 脚本加载检查
  if (!t3cSdkReady) {
    return {
      canCall: false,
      errorMsg: `tccc SDK脚本加载中，请稍后再试。tabUUID: ${curTabUUID}; userId: ${userEmail};当前时间: ${new Date().toLocaleString()}`,
    }
  }

  // T3C 就绪检查
  if (!t3cReady) {
    return {
      canCall: false,
      errorMsg: `tccc未就绪,请稍后再试。${debugInfo}`,
    }
  }

  // T3C 失败检查
  if (t3cfailed) {
    return {
      canCall: false,
      errorMsg: `T3C 失败: ${t3cErrorMsg}。${debugInfo}`,
    }
  }

  // 坐席状态检查
  const agentStatus = window.tccc?.Agent?.getStatus()
  console.log('当前坐席状态:', agentStatus)

  if (agentStatus === 'busy') {
    return {
      canCall: false,
      errorMsg: `正在呼叫中，请稍后再试。${debugInfo}`,
    }
  }

  if (agentStatus === 'offline' && t3cReady) {
    // 需要重新初始化
    reinitT3cSdk({ refresh: true })
    return {
      canCall: false,
      errorMsg: `当前坐席已在其他地方登录，正在重新登录，请稍后再试。${debugInfo}`,
      needReinit: true,
    }
  }

  return { canCall: true }
}

/**
 * 显示 T3C 检查结果消息
 * @param result 检查结果
 * @returns 是否可以继续外呼
 */
export function showT3CCheckMessage (result: T3CCheckResult): boolean {
  if (!result.canCall) {
    if (result.needReinit) {
      message.success(result.errorMsg)
      console.log('当前坐席已在其他地方登录，正在重新登录，请稍后再试')
    } else {
      message.error(result.errorMsg)
    }
    return false
  }
  return true
}

/**
 * T3C 外呼参数
 */
export interface T3CCallParams {
  phoneNumber: string
  uui?: string
  skillGroupId?: string
  callerPhoneNumber?: string
}

/**
 * 执行 T3C 外呼
 * @param params 外呼参数
 * @param debugInfo 调试信息（用于错误提示）
 * @returns Promise<boolean> 是否成功
 */
export async function startT3CCall (params: T3CCallParams, debugInfo?: string): Promise<boolean> {
  try {
    console.log('外呼参数:', params)
    await window.tccc?.Call?.startOutboundCall(params)
    return true
  } catch (e) {
    const errorMsg = debugInfo
      ? `T3C外呼失败(${e?.message})。${debugInfo}`
      : `T3C外呼失败(${e?.message})`
    message.error(errorMsg)
    console.log('T3C外呼失败', e)
    return false
  }
}

export function injectTcccWebSDK (SdkURL: string, callback: Function, userInfo: UserInfo) {
  // if (window.tccc) {
  //   console.warn('已经初始化SDK了，请确认是否重复执行初始化')
  //   return
  // }
  const script = document.createElement('script')
  script.setAttribute('crossorigin', 'anonymous')
  script.dataset.enableShared = 'true'
  script.dataset.disablePreloadMic = '1'
  script.src = SdkURL
  document.body.appendChild(script)
  script.addEventListener('load', () => {
    callback()
  })

  // 监听加载失败事件
  script.addEventListener('error', async (event) => {
    const eventId = Date.now()
    window.AegisV2?.reportEvent({
      name: 'tccc_sdk_load_error',
      type: 'custom_event' as LogType,
      msg: `tccc sdk 加载失败: ${SdkURL}`,
      eventId,
      user: userInfo?.salesName,
    })

    // 使用 fetch 再次请求 SDK URL，获取 HTTP 状态码
    let httpStatus: number | string = 'unknown'
    try {
      const response = await fetch(SdkURL, { method: 'GET', mode: 'cors' })
      httpStatus = response.status
      const statusText = response.statusText
      window.AegisV2?.reportEvent({
        name: 'tccc_sdk_fetch_load_result',
        type: 'custom_event' as LogType,
        msg: `tccc sdk fetch 加载结果:httpStatus: ${httpStatus}, statusText: ${statusText}`,
        eventId,
        user: userInfo?.salesName,
      })
    } catch (fetchError) {
      window.AegisV2?.reportEvent({
        name: 'tccc_sdk_fetch_load_error',
        type: 'custom_event' as LogType,
        msg: `tccc sdk fetch 加载失败: ${JSON.stringify(fetchError)}`,
        eventId,
        user: userInfo?.salesName,
      })
    }
  })
}
